import { Formik, Form } from "formik";
import * as Yup from "yup";
import { useState, useRef, useEffect } from "react";
import { Modal } from "flowbite-react";
import { format } from "date-fns";

import InputFormik from "@/common/components/form/InputFormik";
import MetaTextareaFormik from "@/common/components/form/MetaTextareaFormik";
import ButtonWithLoader from "@/common/components/ui/loading/ButtonLoading";

import {
  useCreateMessageTemplate,
  useUpdateMessageTemplate,
  useUploadMetaMedia,
} from "@/common/hooks";

import { IMessagesTemplate } from "@/types/messages-template.type";

interface FormMessagesTemplateProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (data: IMessagesTemplate) => void;
  initialData?: IMessagesTemplate;
}

type MediaFile = {
  id: string;
  file?: File;
  url: string;
  type: string;
  name: string;
  size: string;
  assetHandle?: string;
  isUploading?: boolean;
};

const validationSchema = Yup.object({
  name: Yup.string().required("O nome é obrigatório"),
  message: Yup.string().required("A mensagem é obrigatória"),
});

export function FormMessagesTemplate({
  isOpen,
  onClose,
  initialData,
}: FormMessagesTemplateProps) {
  const { mutate: createTemplate, isPending: isCreating } = useCreateMessageTemplate();
  const { mutate: updateTemplate, isPending: isUpdating } = useUpdateMessageTemplate();
  const { mutateAsync: uploadMetaMedia } = useUploadMetaMedia();

  const isPending = isCreating || isUpdating;
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [mediaFiles, setMediaFiles] = useState<MediaFile[]>([]);

  const bodyComponent = initialData?.components?.find((c) => c.type === "BODY");
  const headerComponent = initialData?.components?.find((c) => c.type === "HEADER");

  useEffect(() => {
    if (isOpen && headerComponent?.example?.header_handle?.[0]) {
      const existingUrl = headerComponent.example.header_handle[0];
      const isVideo = existingUrl.toLowerCase().match(/\.(mp4|mov|avi|wmv)$/) || existingUrl.includes("video");

      setMediaFiles([
        {
          id: "initial",
          url: existingUrl,
          type: isVideo ? "video/mp4" : "image/jpeg",
          name: "Mídia Atual",
          size: "-",
          assetHandle: existingUrl,
          isUploading: false,
        },
      ]);
    } else if (isOpen) {
      setMediaFiles([]);
    }
  }, [isOpen, initialData, headerComponent]);

  const initialValues = {
    name: initialData?.name || "",
    message: bodyComponent?.text || "",
  };

  const handleMediaChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;

    const file = files[0];
    const type = file.type;

    const media: MediaFile = {
      id: crypto.randomUUID(),
      file,
      url: URL.createObjectURL(file),
      type,
      name: file.name,
      size: `${(file.size / 1024 / 1024).toFixed(2)} MB`,
      isUploading: true,
    };

    setMediaFiles([media]);

    try {
      const { data } = await uploadMetaMedia(file);
      setMediaFiles([
        {
          ...media,
          assetHandle: data.assetHandle,
          isUploading: false,
        },
      ]);
    } catch {
      setMediaFiles([]);
      alert("Erro ao enviar mídia para o Meta");
    }
  };

  const removeMedia = () => {
    setMediaFiles([]);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const handleSubmit = async (values: typeof initialValues) => {
    const media = mediaFiles[0];

    const payload = {
      name: values.name,
      message: values.message,
      media: {}
    };

    if (media?.assetHandle) {
      payload.media = {
        assetHandle: media.assetHandle,
        mimeType: media.type,
      };
    }

    if (initialData?.id) {
      updateTemplate({ ...payload, id: initialData.id });
    } else {
      createTemplate(payload);
    }

    onClose();
  };

  const isUploadingMedia = mediaFiles.some((m) => m.isUploading);

  return (
    <Modal show={isOpen} onClose={onClose} size="7xl">
      <Modal.Header>
        <h3 className="text-xl font-semibold dark:text-black">
          {initialData ? "Editar Template" : "Criar Template do Meta"}
        </h3>
      </Modal.Header>

      <Modal.Body>
        <Formik
          initialValues={initialValues}
          validationSchema={validationSchema}
          onSubmit={handleSubmit}
          enableReinitialize
        >
          {({ values }) => (
            <Form>
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 p-6">
                <div className="lg:col-span-2 space-y-6">
                  <div className="bg-white p-6 rounded-lg border">
                    <InputFormik
                      label="Nome do Template"
                      name="name"
                      placeholder="ex: promocao_outubro"
                    />

                    <div className="mt-6">
                      <label className="block mb-2 text-sm font-medium text-gray-900">
                        Mensagem
                      </label>
                      <MetaTextareaFormik name="message" />
                      <div className="mt-2 text-right text-sm text-gray-500">
                        {values.message.length}/1000 caracteres
                      </div>
                    </div>
                  </div>

                  <div className="bg-white p-6 rounded-lg border">
                    <h4 className="mb-4 text-lg font-semibold">
                      Mídia (Header opcional)
                    </h4>

                    <input
                      ref={fileInputRef}
                      type="file"
                      accept="image/*,video/*"
                      onChange={handleMediaChange}
                      className="hidden"
                    />

                    <button
                      type="button"
                      onClick={() => fileInputRef.current?.click()}
                      className="w-full py-4 border-2 border-dashed rounded-lg hover:bg-gray-50 transition-colors"
                    >
                      Adicionar imagem ou vídeo
                    </button>

                    {mediaFiles.map((media) => (
                      <div
                        key={media.id}
                        className="w-52 mt-4 border rounded-lg overflow-hidden bg-gray-50"
                      >
                        {media.type.startsWith("image") ? (
                          <img src={media.url} className="w-52 h-32 object-cover" alt="Preview" />
                        ) : (
                          <video src={media.url} className="w-52 h-32 object-cover" />
                        )}

                        <div className="p-2 flex justify-between items-center text-xs">
                          <span className="truncate flex-1 mr-2">
                            {media.isUploading ? "Enviando..." : media.name}
                          </span>
                          <button
                            type="button"
                            onClick={removeMedia}
                            className="text-red-600 font-bold px-2 py-1 hover:bg-red-50 rounded"
                          >
                            X
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                <div
                  className="min-h-[500px] p-6 flex items-start justify-center rounded-lg"
                  style={{ backgroundColor: "#e5ddd5" }}
                >
                  <div className="bg-white rounded-lg p-2 shadow-md w-full max-w-[300px]">
                    {mediaFiles.length > 0 && (
                      <div className="mb-2">
                        {mediaFiles[0].type.startsWith("image") ? (
                          <img
                            src={mediaFiles[0].url}
                            className="rounded w-full object-cover max-h-48"
                            alt="Preview Template"
                          />
                        ) : (
                          <video
                            src={mediaFiles[0].url}
                            controls
                            className="rounded w-full max-h-48"
                          />
                        )}
                      </div>
                    )}

                    <div className="text-[14px] whitespace-pre-wrap px-1 pt-1">
                      <span className="break-words text-gray-800">
                        {values.message || "Sua mensagem aqui..."}
                      </span>
                    </div>

                    <div className="text-[10px] text-gray-400 text-right mt-1">
                      {format(new Date(), "HH:mm")}
                    </div>
                  </div>
                </div>
              </div>

              <div className="flex justify-end gap-3 p-6 border-t bg-gray-50">
                <button
                  type="button"
                  onClick={onClose}
                  disabled={isPending || isUploadingMedia}
                  className="px-6 py-2 text-sm font-medium bg-white border rounded-lg hover:bg-gray-100 disabled:opacity-50"
                >
                  Cancelar
                </button>

                <ButtonWithLoader
                  type="submit"
                  isLoading={isPending || isUploadingMedia}
                >
                  {initialData ? "Atualizar Template" : "Salvar Template"}
                </ButtonWithLoader>
              </div>
            </Form>
          )}
        </Formik>
      </Modal.Body>
    </Modal>
  );
}
